import { Link } from "@inertiajs/react";
import type { Scope } from "@sentry/core";
import type { FallbackRender } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { HotkeysProvider } from "@tanstack/react-hotkeys";
import posthogClient from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import type { ReactNode } from "react";
import { lazy, StrictMode, Suspense, useState } from "react";

import { AppLogoIcon } from "./components/branding/app-logo-icon";
import { PageVisitProgress } from "./components/app/page-visit-progress";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./components/ui/collapsible";
import { AnchoredToastProvider, ToastProvider } from "./components/ui/toast";
import { TooltipProvider } from "./components/ui/tooltip";
import { AppearanceProvider } from "./hooks/use-appearance";
import { useMountEffect } from "./hooks/use-mount-effect";

import type { AppearanceActorRef } from "./lib/flows/appearance-machine";
import { appShellHotkeyOptions } from "./lib/hotkeys";
import { NuqsAdapter } from "./lib/nuqs-inertia-adapter";
import { home } from "./routes";

const reloadPage = () => {
  window.location.reload();
};

const stringifyFallbackError = (error: unknown): string | undefined => {
  try {
    return JSON.stringify(error);
  } catch {
    return undefined;
  }
};

const formatFallbackErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (typeof error === "number" || typeof error === "boolean" || typeof error === "bigint") {
    return String(error);
  }
  if (typeof error === "symbol") {
    return error.description ?? error.toString();
  }

  if (error === undefined) {
    return "undefined";
  }

  if (typeof error === "function") {
    return error.toString();
  }

  const serialized = stringifyFallbackError(error);
  if (serialized !== undefined) {
    return serialized;
  }

  return Object.prototype.toString.call(error);
};

interface RootErrorDetailsProps {
  readonly error: unknown;
  readonly eventId: string | null;
}

const RootErrorEventId = ({ eventId }: { readonly eventId: string }) => (
  <Alert>
    <AlertTitle>Event ID</AlertTitle>
    <AlertDescription>{eventId}</AlertDescription>
  </Alert>
);

const RootErrorDeveloperMessage = ({ error }: { readonly error: unknown }) => (
  <Alert>
    <AlertDescription className="max-h-48 overflow-auto whitespace-pre-wrap">
      {formatFallbackErrorMessage(error)}
    </AlertDescription>
  </Alert>
);

const RootErrorDetails = ({ error, eventId }: RootErrorDetailsProps) => (
  <Collapsible className="w-full">
    <CollapsibleTrigger className="text-xs text-muted-foreground">Details</CollapsibleTrigger>
    <CollapsibleContent className="mt-3 flex flex-col gap-2">
      {eventId === null ? null : <RootErrorEventId eventId={eventId} />}
      {import.meta.env.DEV ? <RootErrorDeveloperMessage error={error} /> : null}
    </CollapsibleContent>
  </Collapsible>
);

const RootErrorHeader = () => (
  <div className="flex flex-col items-center gap-1 text-center">
    <Button size="icon" variant="link" render={<Link href={home()} />}>
      <AppLogoIcon className="size-9" />
    </Button>
    <CardTitle>Something went wrong</CardTitle>
    <CardDescription>Something stopped the page from loading. Reload to try again.</CardDescription>
  </div>
);

const RootErrorActions = () => (
  <div className="flex flex-col gap-2">
    <Button onClick={reloadPage}>Reload page</Button>
    <Button variant="outline" render={<Link href={home()} />}>
      Go home
    </Button>
  </div>
);

const RootErrorFallback: FallbackRender = ({ error, eventId }) => {
  const sentryEventId = eventId === "" ? null : eventId;
  const hasDetails = import.meta.env.DEV || sentryEventId !== null;

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <RootErrorHeader />
        </CardHeader>
        <CardContent>
          <RootErrorActions />
        </CardContent>
        {hasDetails ? (
          <CardFooter>
            <RootErrorDetails error={error} eventId={sentryEventId} />
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
};

const setRootBoundaryTag = (scope: Scope) => {
  scope.setTag("boundary", "root");
};

const DevelopmentAnnotationToolbar = import.meta.env.DEV
  ? lazy(async () => {
      const toolbar = await import("./components/app/development-annotation-toolbar");

      return toolbar;
    })
  : null;

const DevelopmentTools = () => {
  const [mounted, setMounted] = useState(false);

  useMountEffect(() => {
    setMounted(true);
  });

  if (!mounted || DevelopmentAnnotationToolbar === null) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <DevelopmentAnnotationToolbar />
    </Suspense>
  );
};

const AnalyticsProviders = ({ children }: { readonly children: ReactNode }) => (
  <PostHogProvider client={posthogClient}>
    <NuqsAdapter>{children}</NuqsAdapter>
  </PostHogProvider>
);

const ClientProviders = ({
  appearanceActor,
  children,
}: {
  readonly appearanceActor: AppearanceActorRef;
  readonly children: ReactNode;
}) => (
  <AppearanceProvider actor={appearanceActor}>
    <AnalyticsProviders>
      <HotkeysProvider defaultOptions={{ hotkey: appShellHotkeyOptions }}>
        <ToastProvider position="bottom-right">
          <AnchoredToastProvider>
            <TooltipProvider>{children}</TooltipProvider>
          </AnchoredToastProvider>
        </ToastProvider>
      </HotkeysProvider>
    </AnalyticsProviders>
  </AppearanceProvider>
);

export const ClientAppShell = ({
  appearanceActor,
  children,
}: {
  readonly appearanceActor: AppearanceActorRef;
  readonly children: ReactNode;
}) => (
  <StrictMode>
    <Sentry.ErrorBoundary fallback={RootErrorFallback} beforeCapture={setRootBoundaryTag}>
      <ClientProviders appearanceActor={appearanceActor}>
        {children}
        <PageVisitProgress />
        <DevelopmentTools />
      </ClientProviders>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
